// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { queryClient } from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { Alert, Form } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CaseOwnerChangeInput, OrganisationLinksOutput, ProfileOutput } from 'thehive-sdk';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import RouteBuilder from '@Services/RouteBuilder';

import './CaseChangeOwner.scss';

interface CaseChangeOwnerFormValues {
    organisation: string;
    profile: string;
}

interface CaseChangeOwnerProps {
    linkedOrgs: OrganisationLinksOutput[];
    profileList: ProfileOutput[];
    onFinish: (values: CaseOwnerChangeInput) => Promise<void>;
    visible: boolean;
    setVisible: (visible: boolean) => void;
    newOrg?: string;
}

export const CaseChangeOwner = ({
    linkedOrgs,
    profileList,
    onFinish,
    visible,
    setVisible,
    newOrg,
}: CaseChangeOwnerProps) => {
    const i = useLocalMessage();

    const history = useHistory();
    const { caseId } = useParams<{ caseId: string }>();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched, loading, setLoading } = useValidateForm(
        { isValidInit: true, fieldsTouchedInit: true },
    );
    const profile = Form.useWatch(['profile'], form);
    const [keepAccess, setKeepAccess] = useState(typeof profile !== 'undefined');

    const onChangeKeepAccess = (e: CheckboxChangeEvent) => {
        setKeepAccess(e.target.checked);
    };

    const linkedOrgsOptions = useMemo(
        () => linkedOrgs.map(({ toOrganisation }) => ({ label: toOrganisation, value: toOrganisation })),
        [linkedOrgs],
    );

    const profileOptions = useMemo(
        () =>
            profileList
                .reduce(
                    (acc, { _id, name, forOrg }) => (forOrg ? [...acc, { label: name, value: _id }] : acc),
                    [] as DefaultOptionType[],
                )
                .sort((a, b) => a.label?.toString().localeCompare(b.label?.toString() || '') || 0),
        [profileList],
    );

    return (
        <THDrawer
            title={i('case.owner.drawer.title')}
            disableClose={fieldsTouched}
            visible={visible}
            className="drawer-form"
            afterVisibleChange={(visible) => {
                form.resetFields();
                setVisible(visible);
                setKeepAccess(false);

                if (visible) {
                    setFieldsTouched(true);
                }
            }}
        >
            {(toggleDrawer) => (
                <THForm<CaseChangeOwnerFormValues>
                    form={form}
                    name="case-change-owner-form"
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={{
                        organisation: newOrg,
                    }}
                    onFinish={({ organisation, profile }) => {
                        setLoading(true);
                        onFinish({
                            keepProfile: profile,
                            organisation,
                        })
                            .then(() => {
                                toggleDrawer();
                                setFieldsTouched(false);
                                queryClient.refetchQueries(['sharings', caseId]);
                            })
                            .finally(() => {
                                setLoading(false);
                                if (!keepAccess) {
                                    history.push(RouteBuilder.compute('cases.default'));
                                }
                            });
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid || !fieldsTouched}
                                loading={loading}
                                onCancel={() => {
                                    setFieldsTouched(false);
                                    toggleDrawer();
                                    form.resetFields();
                                }}
                            />
                        }
                    >
                        <Alert
                            message={i('case.owner.form.newCaseAlert')}
                            type="info"
                            showIcon
                            className="case-change-owner__alert"
                        />

                        <THForm.Item
                            name="organisation"
                            label={i('case.owner.form.organisation.new')}
                            className="case-change-owner__organisation"
                            rules={[{ required: true, message: i('case.owner.form.organisation.required') }]}
                        >
                            <THAutoComplete
                                options={linkedOrgsOptions}
                                placeholder={i('case.owner.form.organisation.placeholder')}
                            />
                        </THForm.Item>

                        <THCheckbox checked={keepAccess} onChange={onChangeKeepAccess}>
                            {i('case.owner.form.keepAccess')}
                        </THCheckbox>

                        {keepAccess && (
                            <THForm.Item
                                name="profile"
                                label={i('case.owner.form.profile.label')}
                                rules={[{ required: keepAccess, message: i('case.owner.form.profile.required') }]}
                            >
                                <THSelect
                                    options={profileOptions}
                                    placeholder={i('case.owner.form.profile.placeholder')}
                                />
                            </THForm.Item>
                        )}
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
